import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { BadgeCheck, Banknote, Calendar, Check, ChevronDown, Clock, Compass, Copy, ExternalLink, Loader2, MapPin, Plus, Quote, Share2 } from "lucide-react";
import { buildClickUrl, buildBookingSearchUrl } from "@/lib/clicks";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useJourneys, type Trip, type Day, type ItineraryItem } from "@/context/JourneyContext";
import { TripMap } from "@/components/TripMap";
import TripEssentials from "@/components/TripEssentials";
import PlacePhoto from "@/components/PlacePhoto";
import { getCoverImage } from "@/lib/trip-helpers";
import { BUCKET_LABELS } from "@/lib/itinerary-display";

const GOOGLE_MAPS_API_KEY: string =
  import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? "";

const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

const formatDateRange = (
  start: string | null | undefined,
  end: string | null | undefined
): string | null => {
  const s = formatDate(start);
  const e = formatDate(end);
  if (s && e) return `${s} – ${e}`;
  return s || e || null;
};

const computeNights = (
  checkIn?: string,
  checkOut?: string
): number | null => {
  if (!checkIn || !checkOut) return null;
  const a = new Date(`${checkIn}T00:00:00`);
  const b = new Date(`${checkOut}T00:00:00`);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
  const diff = Math.round(
    (b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000)
  );
  return diff > 0 ? diff : null;
};

const groupItemsByBucket = (items: ItineraryItem[]) => {
  const groups: Record<string, ItineraryItem[]> = {
    morning: [],
    afternoon: [],
    evening: [],
    night: [],
  };
  for (const item of items) {
    (groups[item.timeBucket] ?? groups.morning).push(item);
  }
  return groups;
};

const generateUuid = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `journey-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const SharedTrip = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { saveJourney, journeys } = useJourneys();
  // The stamp's Nº is this trip's place on the user's shelf.
  const journeyCount = journeys.length;
  const { user, isConfigured, signInWithGoogle } = useAuth();

  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  // The copy flow is two steps, and sign-in is an offer at the end, never a
  // gate: confirm -> copied.
  const [copyStep, setCopyStep] = useState<null | "confirm" | "done">(null);
  const [keepDates, setKeepDates] = useState(true);
  const [copiedTripId, setCopiedTripId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Remove this shared trip from our servers (GDPR erasure). Possession
  // of the share id is the authorization (no accounts). Two-tap confirm
  // via window.confirm keeps it dependency-light.
  const handleDeleteShare = async () => {
    if (!id || deleting) return;
    setConfirmDelete(false);
    setDeleting(true);
    try {
      const res = await fetch("/api/delete-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Delete failed (${res.status})`);
      }
      toast({
        title: "Shared trip deleted",
        description: "This link no longer works.",
      });
      navigate("/");
    } catch (err) {
      toast({
        title: "Couldn't delete",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    fetch(`/api/share?id=${id}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(
            data?.error ||
              (res.status === 404
                ? "Trip not found or has expired."
                : "Failed to load trip.")
          );
        }
        return data;
      })
      .then((data) => {
        // Server returns { trip }; older versions returned the trip directly.
        const payload: Trip = data?.trip ?? data;
        // Validate the shape before trusting it. A 200 carrying something
        // that is not a trip (a proxy's HTML fallback, a truncated body)
        // would otherwise crash the page on first render rather than
        // showing the error state below.
        if (!payload || !Array.isArray(payload.days)) {
          throw new Error("Trip not found or has expired.");
        }
        setTrip(payload);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement("input");
      input.value = window.location.href;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSaveAsMyOwn = () => {
    if (!trip) return;
    const newId = generateUuid();
    const now = new Date().toISOString();
    const cloned: Trip = {
      ...trip,
      id: newId,
      // Re-stamp ownership timestamps; chat history is empty (this is a fresh
      // editable copy, not a continuation of the original planner's chat).
      chatHistory: [
        {
          id: `msg-${Date.now()}`,
          role: "assistant",
          content: `I've imported this shared trip${
            trip.destination ? ` to ${trip.destination}` : ""
          }. Want to tweak anything, swap days around, or add new stops?`,
          timestamp: Date.now(),
        },
      ],
      createdAt: now,
      updatedAt: now,
      // Dropping the dates keeps the plan and its order, and lets the owner
      // re-date it later.
      ...(keepDates ? {} : { startDate: undefined, endDate: undefined }),
    };
    saveJourney(cloned);
    setCopiedTripId(newId);
    setCopyStep("done");
  };

  // Both non-trip states are the postcard itself rather than a centred
  // message: the dark "Shared plan" strip, the frame, and the same shadow
  // the real card uses. Shared by the skeleton and the returned stamp.
  const Postcard = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen bg-paper-deep py-10 px-4">
      <div className="mx-auto max-w-[560px] bg-paper border border-rule shadow-[0_8px_40px_-12px_rgba(var(--fn-shadow-rgb),0.18)] min-h-[560px] flex flex-col">
        <div className="bg-ink text-paper py-[9px] px-4 text-center font-sans text-[9.5px] font-semibold tracking-[0.14em] uppercase">
          Shared plan
        </div>
        {children}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Postcard>
        {/* Diagonal stripes stand in for the map, skeleton bars for the
            title block and the first stops. */}
        <div
          className="h-[150px] border-b border-ink"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg,var(--fn-map) 0,var(--fn-map) 8px,var(--fn-stripe-b) 8px,var(--fn-stripe-b) 16px)",
          }}
        />
        <div className="px-6 pt-[26px] pb-[18px] border-b border-ink flex flex-col items-center gap-2.5">
          <span className="w-[140px] h-[9px] bg-skeleton" />
          <span className="w-[220px] h-[22px] bg-skeleton" />
          <span className="w-[170px] h-[9px] bg-skeleton" />
        </div>
        <div className="px-6 py-[22px] flex flex-col gap-3.5">
          <span className="w-[120px] h-[9px] bg-skeleton" />
          <span className="w-full h-px bg-rule-hair" />
          <span className="w-4/5 h-3 bg-skeleton" />
          <span className="w-full h-px bg-rule-hair" />
          <span className="w-[65%] h-3 bg-skeleton" />
        </div>
        <div className="mt-auto px-6 pb-7 text-center font-hand text-[18px] text-ink-faint">
          fetching the postcard...
        </div>
      </Postcard>
    );
  }

  if (error || !trip) {
    return (
      <Postcard>
        <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-10">
          {/* Returned-to-sender stamp. Decorative: the heading below says
              the same thing to anyone not looking at it. */}
          <div
            aria-hidden="true"
            className="-rotate-[7deg] w-24 h-24 rounded-full border-[2.5px] flex flex-col items-center justify-center gap-0.5 mb-7"
            style={{
              borderColor: "rgba(179,38,30,.55)",
              color: "rgba(179,38,30,.75)",
            }}
          >
            <span className="font-sans text-[8.5px] font-bold tracking-[0.18em] uppercase">
              Sunday Atlas
            </span>
            <span className="font-serif italic text-[13px]">returned</span>
            <span className="font-sans text-[8.5px] font-bold tracking-[0.14em]">
              NO FORWARD ADDRESS
            </span>
          </div>
          <div className="font-sans text-[10px] font-bold tracking-[0.2em] uppercase text-ink-muted mb-2.5">
            Link expired or removed
          </div>
          <h2 className="font-serif font-medium text-[28px] leading-[1.15] text-ink mb-3">
            This postcard didn't make it.
          </h2>
          <p className="font-serif text-[15px] leading-[1.6] text-ink-muted mb-[26px]">
            {error ||
              "The trip was unshared, or the link is wrong. Ask whoever sent it for a fresh one."}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-ink px-[22px] py-[13px] min-h-[44px] font-sans text-[10.5px] font-bold tracking-[0.14em] uppercase text-ink hover:bg-ink hover:text-paper transition-colors"
          >
            Plan your own instead
          </Link>
        </div>
      </Postcard>
    );
  }

  const dateRange = formatDateRange(trip.startDate, trip.endDate);
  const stopCount = trip.days.reduce((n, d) => n + d.items.length, 0);
  const hasVerified = trip.days.some((d) =>
    d.items.some((i) => i.source && /^\d/.test(i.source.label.trim()))
  );
  // The shared page has no geocoder; TripMap falls back to fitting the pins.
  const destinationCenter = undefined;
  // "Shared on <date>" from the stored trip's timestamp.
  const sharedOn = (() => {
    const raw = trip.updatedAt || trip.createdAt;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  })();
  const sharedOnLabel = sharedOn
    ? sharedOn.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;
  // The postmark takes month and year only; at 8.5px inside a 96px ring the
  // day number and its comma crowd the circle.
  const postmarkLabel = sharedOn
    ? sharedOn.toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : null;
  const dayCount = trip.days.length;

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div className="min-h-screen flex flex-col bg-paper-deep text-ink">
        {/* Banner: this is somebody's postcard, not your planner. */}
        <div className="bg-ink text-paper px-6 py-2.5 text-center font-sans text-[11px] font-semibold uppercase tracking-[0.14em]">
          Read-only postcard ·{" "}
          <Link to="/" className="text-accent-on-dark underline underline-offset-[3px] hover:text-on-accent">
            plan your own trip
          </Link>
        </div>

        <header className="bg-paper border-b border-ink">
          <div className="mx-auto max-w-[1080px] px-5 md:px-12 py-4 flex items-center justify-between gap-3">
            <Link to="/" className="flex items-center gap-2.5 shrink-0 min-h-[44px]">
              <span aria-hidden="true" className="h-2 w-2 rounded-full bg-accent" />
              <span className="font-sans text-xs font-bold uppercase tracking-[0.32em] text-ink">
                Sunday Atlas
              </span>
            </Link>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleCopyLink}
                aria-label="Copy link"
                title={copied ? "Link copied" : "Copy link"}
                className="h-11 w-11 inline-flex items-center justify-center text-ink-faint hover:text-ink transition-colors"
              >
                {copied ? <Check className="h-4 w-4 text-accent-text" /> : <Share2 className="h-4 w-4" />}
              </button>
              <button
                onClick={() => setCopyStep("confirm")}
                className="inline-flex items-center gap-[7px] rounded-full border border-ink px-[18px] min-h-[44px] font-sans text-[11px] font-bold uppercase tracking-[0.14em] text-ink hover:bg-ink hover:text-paper transition-colors"
              >
                <Copy className="h-3.5 w-3.5" />
                Copy to my trips
              </button>
            </div>
          </div>
        </header>

        {/* The postcard itself: cover with its stamp, a centred title block,
            then the day ledger against a sticky map and the owner's note. */}
        <main className="flex-1 px-5 md:px-6 pt-10 md:pt-12">
          <div className="mx-auto max-w-[1080px] bg-paper border border-rule shadow-[0_20px_50px_-18px_rgba(var(--fn-shadow-rgb),0.3)]">
            <div className="relative">
              <img
                src={trip.coverImage || getCoverImage(trip.destination || trip.title || "")}
                alt=""
                className="w-full h-[220px] md:h-[300px] object-cover block border-b border-ink"
              />
              {/* Decorative postmark; it repeats what the title block says. */}
              <div
                aria-hidden="true"
                className="absolute top-4 left-4 -rotate-6 h-24 w-24 rounded-full border-[2.5px] flex flex-col items-center justify-center gap-0.5 pointer-events-none"
                style={{
                  borderColor: "rgba(250,249,246,.85)",
                  color: "rgba(250,249,246,.92)",
                  textShadow: "0 1px 6px rgba(0,0,0,.35)",
                }}
              >
                <span className="font-sans text-[8.5px] font-bold uppercase tracking-[0.18em]">Sunday Atlas</span>
                <span className="font-serif italic text-[13px]">shared plan</span>
                <span className="font-sans text-[8.5px] font-bold tracking-[0.14em] uppercase">
                  {postmarkLabel ?? ""}
                </span>
              </div>
            </div>

            <div className="px-6 md:px-[72px] pt-9 md:pt-11 pb-8 text-center border-b border-ink">
              {trip.destination && (
                <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-ink-muted mb-3.5">
                  {trip.destination}
                </div>
              )}
              <h1 className="font-serif text-[32px] md:text-[52px] leading-[1.05] mb-3">
                {trip.title || trip.destination || "Untitled trip"}
              </h1>
              <div className="flex items-center justify-center gap-3.5 font-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-muted flex-wrap">
                <span aria-hidden="true" className="hidden sm:block h-px w-14 bg-ink" />
                {[dateRange, dayCount ? `${dayCount} ${dayCount === 1 ? "day" : "days"}` : null,
                  stopCount ? `${stopCount} ${stopCount === 1 ? "stop" : "stops"}` : null]
                  .filter(Boolean)
                  .join(" · ")}
                <span aria-hidden="true" className="hidden sm:block h-px w-14 bg-ink" />
              </div>
              {hasVerified && (
                <div className="mt-3.5 inline-flex items-center gap-[5px] font-sans text-[11px] font-bold uppercase tracking-[0.14em] text-accent-text">
                  <BadgeCheck className="h-3 w-3" />
                  Restaurants, bars and stays verified on Google Places
                </div>
              )}
            </div>

            <div className="grid lg:grid-cols-[1fr_330px]">
              <div className="px-6 md:px-14 py-9 md:py-11 lg:border-r border-rule">
                {/* A share of a plan that has not been filled in yet is a
                    designed state, not a blank column. */}
                {stopCount === 0 && (
                  <div className="relative w-full border-[1.5px] border-dashed border-rule-dash px-5 py-[34px] text-center">
                    <span className="absolute -top-[9px] left-1/2 -translate-x-1/2 bg-paper px-2.5 font-sans text-[9px] font-bold tracking-[0.2em] uppercase text-ink-faint">
                      Day 1
                    </span>
                    <div className="font-hand text-[22px] leading-[1.3] text-accent-text -rotate-2 mb-2">
                      nothing scheduled yet
                    </div>
                    <p className="font-serif text-[14px] leading-[1.6] text-ink-muted">
                      This one is still being drafted. Come back once the pages
                      fill in.
                    </p>
                  </div>
                )}
                {trip.days.map((day, i) => (
                  <div key={day.id} className="mb-8">
                    <div className="flex items-baseline gap-3.5 mb-1">
                      <span className="font-sans text-[11px] font-bold uppercase tracking-[0.2em] text-accent-text whitespace-nowrap shrink-0">
                        Day {i + 1}
                        {day.date ? ` · ${formatDate(day.date)}` : ""}
                      </span>
                      <span className="flex-1 border-b border-rule" />
                    </div>
                    <h2 className="font-serif text-[22px] md:text-[26px] mb-3">
                      {day.title || `Day ${i + 1}`}
                    </h2>
                    {day.items.map((item) => (
                      <div
                        key={item.id}
                        className="border-t border-rule-hair py-3 px-0.5 flex gap-3.5 items-baseline"
                      >
                        <span className="font-serif italic text-sm text-ink-faint w-[46px] shrink-0">
                          {item.specificTime || BUCKET_LABELS[item.timeBucket]}
                        </span>
                        <div className="min-w-0 flex-1">
                          <span className="font-serif text-[16.5px]">{item.title}</span>
                          {item.description && (
                            <span className="text-xs text-ink-muted ml-2.5">{item.description}</span>
                          )}
                        </div>
                        {item.source && /^\d/.test(item.source.label.trim()) && (
                          <span className="font-sans text-[10px] font-bold uppercase tracking-[0.12em] text-accent-text inline-flex items-center gap-1 whitespace-nowrap shrink-0">
                            <BadgeCheck className="h-[11px] w-[11px]" />
                            {item.source.label}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <aside className="flex flex-col border-t lg:border-t-0 border-rule lg:sticky lg:top-0 lg:self-start lg:h-screen lg:max-h-[760px]">
                <div className="relative min-h-[340px] flex-1">
                  <TripMap
                    days={trip.days}
                    destination={trip.destination}
                    destinationCenter={destinationCenter}
                    expandedItemId={expandedItemId}
                    onPinClick={setExpandedItemId}
                    onClearSelection={() => setExpandedItemId(null)}
                  />
                </div>
                <div className="border-t border-rule px-6 py-6 bg-paper-warm">
                  <div className="font-hand text-[20px] leading-[1.3] text-accent-text mb-1.5 -rotate-[1.5deg]">from the planner</div>
                  <p className="font-serif text-[14.5px] leading-[1.6] text-ink-soft mb-4">
                    Shared as a snapshot. It will not change if the original trip does.
                  </p>
                  <Link
                    to="/"
                    className="flex items-center justify-center gap-2 bg-ink text-paper rounded-full py-3.5 min-h-[44px] font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] hover:text-accent-on-dark transition-colors"
                  >
                    Plan one like this
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </main>

        {/* Footer: the read-only note, the share date, and the delete link.
            Anyone holding the link can remove the share, since possession of
            the id is the authorization. */}
        <footer className="mx-auto max-w-[1080px] w-full px-5 md:px-12 mt-3.5 pb-16 flex items-center justify-center gap-[18px] flex-wrap font-serif italic text-[13px] text-ink-faint">
          <span>
            Shared plans are read-only.{sharedOnLabel ? ` Shared on ${sharedOnLabel}.` : ""}
          </span>
          <button
            onClick={() => setConfirmDelete(true)}
            disabled={deleting}
            className="rule-link font-sans not-italic text-[10px] font-bold uppercase tracking-[0.14em] text-ink-faint hover:text-danger transition-colors -my-3.5 disabled:opacity-50"
          >
            {deleting ? "Deleting..." : "Delete this shared trip"}
          </button>
        </footer>

        {/* Copy flow, step 1: confirm. Sign-in is never asked for here. */}
        <Dialog open={copyStep === "confirm"} onOpenChange={(o) => !o && setCopyStep(null)}>
          <DialogContent className="max-w-[440px] rounded-none bg-paper border-rule p-7 pb-6 gap-0 [&>button.absolute]:hidden">
            <div className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-accent-text mb-2.5">
              Copy to my trips
            </div>
            <h3 className="font-serif text-[26px] leading-[1.15] mb-2.5">
              Make this {trip.destination || "trip"} your own?
            </h3>
            <p className="font-serif text-[14.5px] leading-[1.6] text-ink-muted mb-4">
              Every stop, hour and booking link comes with it. Edits from here on are yours alone.
            </p>
            {(trip.startDate || trip.endDate) && (
              <div className="border-y border-rule py-3 mb-[18px] flex flex-col gap-2.5">
              <button
                type="button"
                onClick={() => setKeepDates((k) => !k)}
                className="flex items-center gap-[9px] text-left min-h-[44px] text-[13px] text-ink"
              >
                <span className={`relative h-[15px] w-[15px] shrink-0 border-[1.5px] ${keepDates ? "border-accent" : "border-rule-dash"}`}>
                  {keepDates && (
                    <svg viewBox="0 0 16 16" fill="none" className="absolute -top-[3px] -left-[2px] h-[19px] w-[19px]">
                      <path d="M2 9 C5 12, 6 13, 7 11 C9 6, 11 3, 15 1" stroke="var(--fn-accent)" strokeWidth="2.4" strokeLinecap="round" />
                    </svg>
                  )}
                </span>
                Keep the original dates{dateRange ? ` (${dateRange})` : ""}
              </button>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleSaveAsMyOwn}
                className="flex-1 font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] bg-ink text-paper rounded-full py-3.5 min-h-[44px] hover:text-accent-on-dark transition-colors"
              >
                Copy trip
              </button>
              <button
                onClick={() => setCopyStep(null)}
                className="font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] border border-rule-dash-2 rounded-full px-[18px] py-3.5 min-h-[44px] text-ink-muted hover:border-ink hover:text-ink transition-colors"
              >
                Cancel
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Copy flow, step 2: copied. Signing in is offered, never required. */}
        <Dialog open={copyStep === "done"} onOpenChange={(o) => !o && setCopyStep(null)}>
          <DialogContent className="max-w-[440px] rounded-none bg-paper border-rule p-7 pb-6 gap-0 relative overflow-hidden [&>button.absolute]:hidden">
            {/* Decorative stamp: it repeats what the heading already says. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute right-[18px] top-4 rotate-[8deg] h-[84px] w-[84px] rounded-full border-[2.5px] border-[var(--fn-stamp-border)] text-[var(--fn-stamp-text)] flex flex-col items-center justify-center gap-px"
            >
              <span className="font-sans text-[7.5px] font-bold tracking-[0.18em] uppercase">
                Sunday Atlas
              </span>
              <span className="font-serif italic text-[12px]">yours now</span>
              <span className="font-sans text-[7.5px] font-bold tracking-[0.14em]">
                Nº {journeyCount}
              </span>
            </div>
            <div className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-accent-text mb-2.5">
              Copied
            </div>
            <h3 className="font-serif text-[26px] leading-[1.15] mb-2.5 max-w-[220px]">
              {trip.destination || "Your trip"} is on your shelf.
            </h3>
            <p className="font-serif text-[14.5px] leading-[1.6] text-ink-muted mb-[22px]">
              {stopCount} {stopCount === 1 ? "stop" : "stops"},{" "}
              {keepDates && dateRange ? "dates as they were" : "no dates yet"}. Edit anything; it's
              yours now.
            </p>
            <div className="border-t border-rule pt-3.5 flex flex-col gap-2">
              <button
                onClick={() => copiedTripId && navigate(`/journey/${copiedTripId}`)}
                className="font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] bg-ink text-paper rounded-full py-3.5 min-h-[44px] hover:text-accent-on-dark transition-colors"
              >
                Open my copy
              </button>
              {!user && isConfigured && (
                <>
                  <button
                    onClick={() => signInWithGoogle()}
                    className="inline-flex items-center justify-center gap-2.5 font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] border border-ink rounded-full py-3.5 min-h-[44px] text-ink hover:bg-ink hover:text-paper transition-colors"
                  >
                    <span className="font-serif font-semibold text-[14px] tracking-normal normal-case">
                      G
                    </span>
                    Continue with Google
                  </button>
                  <span className="font-hand text-[17px] leading-[1.3] text-ink-faint text-center">
                    sign in to keep it on every device
                  </span>
                </>
              )}
              <button
                onClick={() => navigate("/dashboard")}
                className="text-center font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted hover:text-ink transition-colors py-2.5 min-h-[44px]"
              >
                Back to my trips
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stop sharing. */}
        <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <DialogContent className="max-w-[440px] rounded-none bg-paper border-rule p-7 pb-6 gap-0 [&>button.absolute]:hidden">
            <div className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-danger mb-2.5">
              Delete shared trip
            </div>
            <h3 className="font-serif text-[26px] leading-[1.15] mb-2.5">Stop sharing this trip?</h3>
            <p className="font-serif text-[14.5px] leading-[1.6] text-ink-muted mb-[22px]">
              Delete this shared trip from SundayAtlas's servers? The link will stop working. This
              can't be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleDeleteShare}
                className="flex-1 font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] bg-danger text-on-accent rounded-full py-3.5 min-h-[44px]"
              >
                Delete shared trip
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="font-sans text-[10.5px] font-bold uppercase tracking-[0.14em] border border-rule-dash-2 rounded-full px-[18px] py-3.5 min-h-[44px] text-ink-muted hover:border-ink hover:text-ink transition-colors"
              >
                Cancel
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </APIProvider>
  );
};


export default SharedTrip;
